// B1319: the Access page's server calls. `SHOW GRANTS` lists the policy rules
// of the current database (B347), and GRANT / REVOKE change them. Every
// statement is the SQL of web/docs/sql.md § Users, roles and permissions,
// sent on the shared WS.
//
// A rule is edited with the two statements the server has: GRANT unions the
// rights into the rule with the same target and filter, REVOKE subtracts them
// (`auth_policy.rs` `grant`, `revoke`). So an edit is one GRANT of the added
// rights and one REVOKE of the removed rights.
//
// `SHOW GRANTS` is superuser-only, so a non-root session sees the server's
// `permission denied` in the page, never a fake empty list.

import { wsSql } from './socket.js';

// The five rights, in the bit order of the server. The names are the
// lower-case names that `SHOW GRANTS` answers in its `rights` column.
export const PERMISSIONS = ['select', 'insert', 'update', 'delete', 'invoke'];

// A SQL string literal: single quotes, an embedded quote doubled. A role name
// is a string literal in the grammar (`TO ROLE 'analysts'`).
export function lit(s) {
  return "'" + String(s).replace(/'/g, "''") + "'";
}

// A name as `[name]`. The lexer takes the bracket content verbatim, but it
// opens an identifier only when the first character is an ASCII letter or `_`
// (else `[2024]` is a vector), it stops at the first `]`, and it trims the
// content (`token.rs` `bracket_opens_ident`, `read_quoted_ident`). A name
// that no bracket spelling reaches is refused here, not sent.
export function quoteName(name) {
  const s = String(name ?? '');
  if (s === '') throw new Error('a name cannot be empty');
  if (s.includes(']')) throw new Error(`the name ${JSON.stringify(s)} holds a \`]\`, which no SQL spelling reaches`);
  if (s.trim() !== s) throw new Error(`the name ${JSON.stringify(s)} starts or ends with a space, which no SQL spelling keeps`);
  if (!/^[A-Za-z_]/.test(s)) throw new Error(`the name ${JSON.stringify(s)} must start with a letter or \`_\``);
  return `[${s}]`;
}

// The segments of a canonical field path, the mirror of `split_field_path`
// (crates/kinedb-query/src/ast.rs): `\.` is a dot inside a key, `\\` is one
// backslash, and any other `\x` keeps its backslash.
function splitFieldPath(path) {
  const out = [];
  let cur = '';
  for (let i = 0; i < path.length; i++) {
    const c = path[i];
    if (c === '\\') {
      const next = path[i + 1];
      if (next === '.' || next === '\\') { cur += next; i++; }
      else if (next === undefined) cur += '\\';
      else { cur += '\\' + next; i++; }
    } else if (c === '.') {
      out.push(cur); cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

// A canonical field path (`address.city`, `lines.0.sku`) written back as SQL:
// `[address].[city]`, `[lines][0].[sku]`. A position follows its field with
// no dot; the first segment is a field, never a position.
export function fieldPathSql(canonical) {
  const segs = splitFieldPath(String(canonical ?? ''));
  let sql = '';
  segs.forEach((seg, i) => {
    if (/^\d+$/.test(seg)) {
      if (i === 0) throw new Error(`the field path ${JSON.stringify(canonical)} starts with a position`);
      sql += `[${seg}]`;
    } else {
      sql += (i === 0 ? '' : '.') + quoteName(seg);
    }
  });
  return sql;
}

function rightsSql(rights) {
  const set = new Set(rights ?? []);
  const known = PERMISSIONS.filter((p) => set.has(p));
  if (known.length === 0) throw new Error('pick at least one right');
  if (known.length === PERMISSIONS.length) return 'ALL';
  return known.map((p) => p.toUpperCase()).join(', ');
}

export function targetSql(target) {
  switch (target?.kind) {
    case 'anyone': return 'ANYONE';
    case 'authenticated': return 'AUTHENTICATED';
    case 'role':
      if (!target.name) throw new Error('pick a role');
      return `ROLE ${lit(target.name)}`;
    case 'match':
      if (!target.field) throw new Error('name the field that MATCH compares');
      return `MATCH ${fieldPathSql(target.field)}`;
    default:
      throw new Error(`no GRANT or REVOKE names the target ${JSON.stringify(target?.kind)}`);
  }
}

// ` WHERE <field> = <literal>`, the whole grammar of a policy filter
// (`parser.rs` `parse_policy_where`): one field, a string or an integer.
function whereSql(where) {
  if (!where) return '';
  if (!where.field) throw new Error('name the field of the WHERE');
  let value;
  if (where.type === 'integer') {
    const v = String(where.value ?? '').trim();
    if (!/^-?\d+$/.test(v)) throw new Error(`${JSON.stringify(where.value)} is not an integer`);
    value = v;
  } else {
    value = lit(where.value ?? '');
  }
  return ` WHERE ${fieldPathSql(where.field)} = ${value}`;
}

function policySql(verb, prep, { collection, rights, target, where }) {
  return `${verb} ${rightsSql(rights)} ON ${quoteName(collection)} ${prep} ${targetSql(target)}${whereSql(where)}`;
}

export function grantSql(rule) { return policySql('GRANT', 'TO', rule); }
export function revokeSql(rule) { return policySql('REVOKE', 'FROM', rule); }

// The `grant` column: `ANYONE`, `AUTHENTICATED`, `ROLE <name>`,
// `MATCH <canonical path>` or `PRINCIPAL <id>` (a rule of a dropped role).
export function parseTarget(grant) {
  const g = String(grant ?? '');
  if (g === 'ANYONE') return { kind: 'anyone' };
  if (g === 'AUTHENTICATED') return { kind: 'authenticated' };
  if (g.startsWith('ROLE ')) return { kind: 'role', name: g.slice(5) };
  if (g.startsWith('MATCH ')) return { kind: 'match', field: g.slice(6) };
  if (g.startsWith('PRINCIPAL ')) return { kind: 'principal', id: g.slice(10) };
  return { kind: 'unknown', text: g };
}

// `SHOW GRANTS` → one object per rule, read by column name.
//
// B1376: a row whose `kind` is `function` is a `GRANT INVOKE ON FUNCTION` rule.
// This view builds collection SQL only, so it leaves those rows out. An older
// server sends no `kind` column: every row is then a collection row.
export function parseGrants(r) {
  const cols = r?.columns ?? [];
  const kind = cols.indexOf('kind');
  const rows = (r?.data ?? []).filter((row) => kind < 0 || row[kind] !== 'function');
  return rows.map((row) => {
    const o = {};
    cols.forEach((c, i) => { o[c] = row[i]; });
    const have = new Set(String(o.rights ?? '').split(',').map((s) => s.trim()).filter(Boolean));
    return {
      collection: o.collection,
      rights: PERMISSIONS.filter((p) => have.has(p)),
      grant: o.grant,
      where: o.where ?? null,
      target: parseTarget(o.grant),
    };
  });
}

const SET_POLICY = 'only ALTER COLLECTION … SET POLICY changes it';

// The `where` column (compact JSON) as the `{field, value, type}` of
// `whereSql`, or a reason when `WHERE <field> = <literal>` cannot spell it.
// The JSON must survive a round trip unchanged: `5.0` and an integer past
// 2^53 read back as a different text, so no literal reaches the same filter.
function parseWhere(raw) {
  if (raw == null) return { where: null };
  let obj;
  try { obj = JSON.parse(raw); } catch { return { reason: `the filter ${raw} is not JSON; ${SET_POLICY}` }; }
  const keys = obj && typeof obj === 'object' && !Array.isArray(obj) ? Object.keys(obj) : [];
  const v = keys.length === 1 ? obj[keys[0]] : undefined;
  const ok = keys.length === 1
    && (typeof v === 'string' || Number.isSafeInteger(v))
    && JSON.stringify(obj) === raw;
  if (!ok) return { reason: `the filter ${raw} has no WHERE <field> = <literal> spelling; ${SET_POLICY}` };
  return { where: { field: keys[0], value: String(v), type: typeof v === 'string' ? 'string' : 'integer' } };
}

// The rule of a row in the shape of `grantSql`, or the reason no REVOKE
// reaches the row.
function ruleOf(row) {
  if (row.target.kind === 'principal') {
    return { reason: `a rule of a dropped role (${row.target.id}): REVOKE refuses an unknown role; ${SET_POLICY}` };
  }
  if (row.target.kind === 'unknown') return { reason: `the target ${row.grant} is not known to the console` };
  const w = parseWhere(row.where);
  if (w.reason) return { reason: w.reason };
  try {
    if (row.target.kind === 'match') fieldPathSql(row.target.field);
    if (w.where) fieldPathSql(w.where.field);
    quoteName(row.collection);
  } catch (e) {
    return { reason: e.message };
  }
  return { rule: { collection: row.collection, target: row.target, where: w.where } };
}

// The REVOKE that removes exactly this row, or the reason there is none.
export function revokeFor(row) {
  const r = ruleOf(row);
  if (r.reason) return { reason: r.reason };
  return { sql: revokeSql({ ...r.rule, rights: row.rights }) };
}

// The statements that change the rights of a row to `newRights`: a GRANT of
// the added rights, then a REVOKE of the removed rights.
export function editSql(row, newRights) {
  const r = ruleOf(row);
  if (r.reason) throw new Error(r.reason);
  const want = new Set(newRights);
  const added = PERMISSIONS.filter((p) => want.has(p) && !row.rights.includes(p));
  const removed = row.rights.filter((p) => !want.has(p));
  const out = [];
  if (added.length) out.push(grantSql({ ...r.rule, rights: added }));
  if (removed.length) out.push(revokeSql({ ...r.rule, rights: removed }));
  return out;
}

export function rulesOfRole(rows, role) {
  return rows.filter((r) => r.target.kind === 'role' && r.target.name === role);
}

// The rules that reach a user, each with the path that reaches it. There is
// no `GRANT … TO USER`: a user gets rights through its roles, ANYONE,
// AUTHENTICATED and MATCH (the field names the user or one of its roles).
// A PRINCIPAL row and the rules of other roles are left out.
export function rulesOfUser(rows, { roles = [] } = {}) {
  const out = [];
  for (const r of rows) {
    const t = r.target;
    let via = null;
    if (t.kind === 'role' && roles.includes(t.name)) via = `role ${t.name}`;
    else if (t.kind === 'anyone') via = 'anyone';
    else if (t.kind === 'authenticated') via = 'every logged-in user';
    else if (t.kind === 'match') via = `rows where ${t.field} names the user`;
    if (via) out.push({ ...r, via });
  }
  return out;
}

export async function listGrants(collection) {
  return parseGrants(await wsSql(collection ? `SHOW GRANTS ON ${quoteName(collection)}` : 'SHOW GRANTS'));
}

// Run the statements in order; the first refusal stops the list.
export async function runSql(list) {
  for (const sql of list) await wsSql(sql);
}

// `SHOW COLLECTIONS` → [{name, type}] (type in lower case).
export async function listCollections() {
  const r = await wsSql('SHOW COLLECTIONS');
  return (r?.data ?? []).map((row) => ({ name: row[0], type: row.length > 1 ? String(row[1]).toLowerCase() : 'table' }));
}
